/* eslint-disable react-refresh/only-export-components */
import { createContext, useContext, useRef, useState, type ReactNode } from 'react';
import { CheckoutContext } from '@/context/CheckoutContext';
import { getAvailabilityResetKey } from '@/utils/paymentMethodAvailability';

interface PaymentMethodAvailabilityContextValue {
  isUnavailable: boolean;
  setUnavailable: (unavailable: boolean) => void;
}

const PaymentMethodAvailabilityContext = createContext<PaymentMethodAvailabilityContextValue | null>(null);

interface PaymentMethodAvailabilityProviderProps {
  blockedForZipCode?: boolean;
  children: ReactNode;
}

export const PaymentMethodAvailabilityProvider = ({
  blockedForZipCode = false,
  children,
}: PaymentMethodAvailabilityProviderProps) => {
  const { paymentMethodSelected, otherMethodSelected } = useContext(CheckoutContext);
  const resetKey = getAvailabilityResetKey({
    paymentMethodSelected,
    otherMethodSelected,
    blockedForZipCode,
  });
  const [isUnavailable, setIsUnavailable] = useState(false);
  const previousResetKeyRef = useRef(resetKey);

  if (previousResetKeyRef.current !== resetKey) {
    previousResetKeyRef.current = resetKey;
    setIsUnavailable(false);
  }

  return (
    <PaymentMethodAvailabilityContext.Provider value={{ isUnavailable, setUnavailable: setIsUnavailable }}>
      {children}
    </PaymentMethodAvailabilityContext.Provider>
  );
};

export const usePaymentMethodAvailability = () => {
  const context = useContext(PaymentMethodAvailabilityContext);
  if (!context) {
    throw new Error('usePaymentMethodAvailability must be used within PaymentMethodAvailabilityProvider');
  }
  return context;
};

/** Optional hook for leaf components that may render outside the provider. */
export const useReportPaymentMethodUnavailable = () => useContext(PaymentMethodAvailabilityContext)?.setUnavailable;

export const useIsPaymentMethodUnavailable = () => useContext(PaymentMethodAvailabilityContext)?.isUnavailable ?? false;
